import { useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import { ReactComponent as AlertIcon } from '@packages/shared/assets/info.svg';
import { LoadingStatuses } from '@packages/shared/core/common/entities';
import { SignInScreenProps } from '@packages/shared/core/quiz/entities';
import { isEmailValid } from '@packages/shared/core/common/utils/string';
import { DataTestId } from '@packages/shared/core/common/constants/dataTestId';
import Header from '@packages/shared/core/common/components/layout/Header';
import ButtonBack from '@packages/shared/core/common/components/common/ButtonBack';
import Button from '@packages/shared/core/common/components/common/Button';
import EmailFieldWithSuggestion from '@packages/shared/core/common/components/common/EmailFieldWithSuggestion';
import Link from '@packages/shared/core/common/components/common/Link';
import TextField from '@packages/shared/core/common/components/common/TextField';
import { PagePath } from '@packages/shared/core/common/constants/routes';

import styles from './SignInScreen.module.scss';

const SignInScreen = ({
  onBack,
  goNext,
  trackValidationError,
  trackForgotPasswordClick,
  content,
  userEmail,
  singInError,
  signInLoadingStatus,
}: SignInScreenProps) => {
  const { title, subtitle } = content;

  const emailRef = useRef<HTMLInputElement>(null);

  const { getFieldProps, handleSubmit, errors, touched, isSubmitting, setFieldValue } = useFormik({
    initialValues: {
      email: userEmail,
      password: '',
    },
    onSubmit: async (data, formikHelpers) => {
      await goNext(data, formikHelpers);
    },
    validate: ({ email, password }) => {
      const err = {};

      if (!email) {
        Object.assign(err, { email: 'Email is required' });
      }

      if (!password) {
        Object.assign(err, { password: 'Password is required' });
      }

      if (!isEmailValid(email)) {
        Object.assign(err, { email: 'Invalid email' });
      }

      return err;
    },
  });

  const emailError = touched.email && !!errors.email ? errors.email : '';
  const passwordError = touched.password && !!errors.password ? errors.password : '';

  useEffect(() => {
    if (emailError) {
      trackValidationError('email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailError, passwordError]);

  useEffect(() => {
    if (passwordError) {
      trackValidationError('password');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordError]);

  const displayErrorBadge = signInLoadingStatus === LoadingStatuses.FAILED && singInError;

  return (
    <>
      <Header className={styles.header} menuClassName={styles.menu}>
        <ButtonBack className={styles.buttonBack} secondary onClick={onBack} />
      </Header>

      <section className={styles.contentWrapper}>
        <div className={styles.content}>
          <h1 className={styles.title}>{title}</h1>

          <p className={styles.subtitle}>{subtitle}</p>

          {displayErrorBadge && (
            <div className={styles.error}>
              <AlertIcon className={styles.alertIcon} />

              <span>{singInError}</span>
            </div>
          )}

          <form className={styles.form} onSubmit={handleSubmit}>
            <EmailFieldWithSuggestion
              id="email"
              {...getFieldProps('email')}
              ref={emailRef}
              error={emailError}
              wrapperClass={emailError ? styles.inputWrapperWithError : styles.inputWrapper}
              placeholder="example@mail.com"
              onSuggestion={(suggest) => setFieldValue('email', suggest)}
              data-testid={DataTestId.EMAIL_FIELD}
            />

            <TextField
              id="password"
              {...getFieldProps('password')}
              error={passwordError}
              placeholder="Enter password"
              type="password"
              wrapperClass={passwordError ? styles.inputWrapperWithError : styles.inputWrapper}
              data-testid={DataTestId.PASSWORD_FIELD}
            />

            <div className={styles.formFooter}>
              <Link
                href={PagePath.SIMPLE_REGISTRATION_FORGOT_PASSWORD}
                passHref
                className={styles.forgotPassword}
                onClick={trackForgotPasswordClick}
              >
                Forgot password?
              </Link>
            </div>

            <Button
              wrapperClassName={styles.buttonWrapper}
              type="submit"
              disabled={isSubmitting}
              className={styles.button}
              loading={isSubmitting}
              data-testid={DataTestId.SUBMIT_BUTTON}
            >
              Continue
            </Button>
          </form>
        </div>
      </section>
    </>
  );
};

export default SignInScreen;
